import { observable, action } from 'mobx';
import { RootStore } from '../rootStore';
import { Currency, TaxMode } from 'enums';

import {
  ADMIN_OPTIONS,
  ADMIN_SIGNATURE,
  ADMIN_TERMINATION,
  ADMIN_FIND_ERROR,
  ADMIN_CHANGE_PHONE,
  ADMIN_CHANGE_EMAIL,
  ADMIN_CHANGE_2FA,
  ADMIN_CHANGE_CLAIM,
  ADMIN_SEND_EVENT,
  ADMIN_DELETE_ACCOUNT,
  ADMIN_ACTIVATE_ACCOUNT,
  ADMIN_CONSTANTS,
  ADMIN_CHANGE_CC,
  ADMIN_TRANSFER_CC,
  ADMIN_CHANGE_ID,
  ADMIN_ADVISOR,
  ADMIN_ADVISOR_CHANGE_PHONE,
  ADMIN_ADVISOR_CHANGE_EMAIL,
  ADMIN_SET_PARTNERCODE,
  ADMIN_GENERATE_PDF,
  ADMIN_GENERATE_BOPO_PDF,
  ADMIN_GENERATE_CSV,
  ADMIN_CHANGE_TAXMODE,
  ADMIN_GENERATE_AMENDMENT,
  ADMIN_REACTIVATE_USER,
  ADMIN_DELETE_STATEMENT,
  ADMIN_ACCOUNT_TRANSACTION,
} from 'api/endpoints';
import { UIStore } from 'stores/uiStore';
import { AmountValue, SingleValueResponse } from 'common/interfaces';

interface Options {
  countryCode: string[];
  taxMode: TaxMode[];
}

interface SignatureState {
  draft?: boolean;
  change?: 'PLAN' | 'FEES' | 'PERSONAL_DATA' | 'WITHDRAWAL' | 'NONE';
  bopoId?: number;
  clientCardId: string;
  status: 'ACTIVE' | 'SIGNED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
}

export class ToolsStore {
  rootStore: RootStore;
  uiStore: UIStore;
  @observable resultDialog: boolean;
  @observable confirmDialog: boolean;
  @observable confirmMessage: string;
  @observable confirmEffect: () => void;
  @observable message: string | { message: string; messageValues?: { [key: string]: string | number } }[];
  @observable messageValues: { [key: string]: string | number } | undefined;
  @observable options: Options | null = null;

  constructor(rootStore: RootStore) {
    this.resultDialog = false;
    this.confirmDialog = false;
    this.message = '';
    this.confirmMessage = '';
    this.confirmEffect = () => {};
    this.messageValues = undefined;
    this.rootStore = rootStore;
    this.uiStore = rootStore.stores.uiStore;
  }

  @action
  fetchOptions = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<Options>(ADMIN_OPTIONS);
    if (response?.data) {
      this.options = response.data;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action showResultDialog = (
    message: string | { message: string; messageValues?: { [key: string]: string | number } }[],
    values?: { [key: string]: string },
  ) => {
    this.message = message;
    this.messageValues = values;
    this.resultDialog = true;
  };
  @action hideResultDialog = () => (this.resultDialog = false);
  @action showConfirmDialog = (message: string, effect: () => void) => {
    this.confirmMessage = message;
    this.confirmEffect = effect;
    this.confirmDialog = true;
  };
  @action hideConfirmDialog = () => (this.confirmDialog = false);

  @action
  showResult = (status: number | undefined, key: string, success: 200 | 202, errors?: number[]) => {
    if (status === success) {
      this.showResultDialog(`backOffice_${key}_done`);
    } else if (!status || !errors || !errors.includes(status)) {
      this.showResultDialog(`backOffice_${key}_error`);
    } else {
      this.showResultDialog(`backOffice_${key}_error_${status}`);
    }
  };

  @action
  checkSignature = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<SignatureState>(ADMIN_SIGNATURE(accountId));
    this.uiStore.setLoading(false);
    if (response?.status === 200 && response.data) {
      const messages = [];
      if (response.data.draft !== undefined && response.data.draft !== null)
        messages.push({
          message: `backOffice_signature_state_result_draft_${response.data.draft}_change_${
            response.data.change && response.data.change !== 'NONE'
          }`,
        });
      if (response.data.change && response.data.change !== 'NONE')
        messages.push({ message: `backOffice_signature_state_result_change_${response.data.change}` });
      if (response.data.bopoId && response.data.bopoId !== -1) {
        messages.push({
          message: 'backOffice_signature_state_result_change_boPoId',
          messageValues: { boPoId: response.data.bopoId },
        });
      } else if (response.data.bopoId === -1) {
        messages.push({ message: `backOffice_signature_state_result_noBopoid` });
      }
      if (response.data.clientCardId && response.data.bopoId !== -1) {
        messages.push({
          message: 'backOffice_signature_state_result_change_clientCardId',
          messageValues: { clientCardId: response.data.clientCardId },
        });
      }
      if (response.data.status)
        messages.push({ message: `backOffice_signature_state_result_status_${response.data.status}` });
      this.showResultDialog(messages);
    } else {
      this.showResultDialog('backOffice_signature_state_error');
    }
  };

  @action
  cancelSignature = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.delete<SingleValueResponse<boolean>>(
      ADMIN_SIGNATURE(accountId),
    );
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'signature_cancel', 200, [400, 404]);
  };

  @action
  cancelRequest = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.delete<SingleValueResponse<boolean>>(
      ADMIN_TERMINATION(accountId),
    );
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'request_cancel', 202);
  };

  @action
  generatePDF = async (accountId: string, type: string, change: boolean) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get(ADMIN_GENERATE_PDF(accountId, type, change));
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'generate_pdf', 200, [400, 404, 500]);
  };

  @action
  generateBoPoPDF = async (bopoId: string, type: string, filename?: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get(ADMIN_GENERATE_BOPO_PDF(bopoId, type, filename));
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'generate_pdf', 200, [400, 404, 500]);
  };

  @action
  generateAmendment = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post(ADMIN_GENERATE_AMENDMENT(accountId));
    this.uiStore.setLoading(false);
    if (response?.status === 200) {
      this.showResultDialog('backOffice_generate_amendment_done');
    } else {
      this.showResultDialog(
        'backOffice_generate_amendment_error',
        response?.errors ? { err: response?.errors?.map((e) => e.message).join('<br />') } : undefined,
      );
    }
  };

  @action
  deleteStatement = async (accountId: string, period: string, type: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.delete<SingleValueResponse<boolean>>(
      ADMIN_DELETE_STATEMENT(accountId),
      { period, type },
    );
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'delete_statement', 200);
  };

  @action
  changePhoneNumber = async (accountId: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CHANGE_PHONE(accountId), { phone: value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'phone', 200, [400, 404]);
  };

  @action
  changeEmail = async (accountId: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CHANGE_EMAIL(accountId), { email: value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'email', 200, [400, 404]);
  };

  @action
  changeTaxMode = async (identifier: string, taxMode: TaxMode) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CHANGE_TAXMODE(identifier.replace('/', '')), {
      taxMode,
    });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'taxMode', 200);
  };

  @action
  reactivateUser = async (email: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_REACTIVATE_USER, { email });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'reactivate_user', 200, [400, 404]);
  };

  @action
  change2FA = async (email: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CHANGE_2FA(encodeURIComponent(email)), { value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, '2fa', 200, [400, 404]);
  };

  @action
  sendEvent = async (accountId: string, events: string[]) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const order = ['CONTRACT', 'SIGNATURES', 'PROCESSING'];
    const response = await post<SingleValueResponse<boolean>>(ADMIN_SEND_EVENT(accountId), {
      event: events.sort((a, b) => order.indexOf(a) - order.indexOf(b)),
    });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'event', 202);
  };

  @action
  findErrorMessage = async (occurenceId: string) => {
    const { get } = this.rootStore.stores.apiStore;
    this.uiStore.setLoading(true);
    const response = await get<{ value: string }>(ADMIN_FIND_ERROR(occurenceId));
    this.uiStore.setLoading(false);
    if (response?.data?.value) {
      this.showResultDialog('backOffice_errMessage_found', { err: response?.data?.value });
    } else {
      this.showResultDialog(`backOffice_errMessage_error${response?.status === 400 ? '_400' : ''}`);
    }
  };

  @action
  changeClaim = async (accountId: string, claim: number, currency: Currency) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CHANGE_CLAIM(accountId), {
      claim: { value: claim, currency: currency },
    });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'claim', 200, [404]);
  };

  @action
  accountTransaction = async (
    accountId: string,
    type: string,
    bucketType: string,
    amount: AmountValue,
    tradeDate: string,
    success: boolean,
  ) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_ACCOUNT_TRANSACTION(accountId), {
      accountId,
      type,
      bucketType,
      amount,
      tradeDate,
      success,
    });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'account_transaction', 200, [400, 404]);
  };

  @action
  activateAccount = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.patch(ADMIN_ACTIVATE_ACCOUNT(accountId));
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'activate_account', 200, [404]);
  };

  @action
  deleteAccount = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.delete<SingleValueResponse<boolean>>(
      ADMIN_DELETE_ACCOUNT(accountId),
    );
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'delete_account', 200, [400, 404]);
  };

  @action
  resetAdvisor = async (realm: string, username: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.patch<SingleValueResponse<boolean>>(
      ADMIN_ADVISOR(realm, username),
    );
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'reset_advisor', 200, [404]);
  };

  @action
  changeAdvisorPhone = async (accountId: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_ADVISOR_CHANGE_PHONE(accountId), { phone: value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'change_advisor_phone', 200, [400, 404]);
  };

  @action
  changeAdvisorEmail = async (accountId: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_ADVISOR_CHANGE_EMAIL(accountId), { email: value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'change_advisor_email', 200, [400, 404]);
  };

  @action
  setPartnerCode = async (realm: string, username: string, partnerCode: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<boolean>>(
      ADMIN_SET_PARTNERCODE(realm, username),
      { partnerCode },
    );
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'set_partnercode', 200);
  };

  @action
  setConstant = async (name: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CONSTANTS, { name, value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'set_constant', 200);
  };

  @action
  generateCsv = async (accountId: string) => {
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get(ADMIN_GENERATE_CSV(accountId));
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'generate_csv', 200);
  };

  @action
  changeCC = async (accountId: string, value: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(ADMIN_CHANGE_CC(accountId), { clientCard: value });
    this.uiStore.setLoading(false);
    this.showResult(response?.status, 'change_cc', 200);
  };

  @action
  transferCC = async (pairs: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);

    const payload = pairs
      .split(',')
      .map((p) => p.trim().split(':') as [string, string])
      .filter((i) => i.length === 2)
      .reduce((prev: { [key: string]: string }, curr: [string, string]) => {
        return { ...prev, [curr[0].trim()]: curr[1].trim() };
      }, {});

    const response = await post<{ value: number }>(ADMIN_TRANSFER_CC, payload);
    this.uiStore.setLoading(false);
    if (response?.status === 200 && response.data) {
      this.showResultDialog('backOffice_transfer_cc_done', { count: response.data.value.toString() });
    } else {
      this.showResultDialog('backOffice_transfer_cc_error');
    }
  };

  @action
  changeID = async (accountId: string, currentIdentifier: string, targetIdentifier: string, citizenship: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.hideConfirmDialog();
    this.uiStore.setLoading(true);
    const response = await post<{ errors: { message: string }[] }>(ADMIN_CHANGE_ID(accountId), {
      currentIdentifier,
      targetIdentifier,
      citizenship,
    });
    this.uiStore.setLoading(false);
    if (response?.status === 200) {
      this.showResultDialog('backOffice_change_id_done');
    } else {
      this.showResultDialog(
        'backOffice_change_id_error',
        response?.errors ? { err: response?.errors?.map((e) => e.message).join('<br />') } : undefined,
      );
    }
  };
}
